import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { licensePlanApi } from '../services/api';
import { showError } from '../utils/showError';
import { extractErrorMessage } from '../utils/errorUtils';
import {
  PageHeader,
  SearchBar,
  LoadingSpinner,
  EmptyState,
  ListContainer,
  Pagination,
  ErrorMessage,
  SuccessMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Button,
  ConfirmationModal,
  Badge,
} from '../components/common';
import { Key, Plus, Edit2, Trash2, Star } from 'lucide-react';
import { formatPlanLimits } from '../constants/licenses';
import ModuleBadges from '../components/licenses/ModuleBadges';

const LicensePlans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState({ show: false, planId: null, planName: '' });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchString(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        pageNumber: currentPage,
        pageSize,
        searchString: searchString || undefined,
      };
      const response = await licensePlanApi.list(params);
      setPlans(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [currentPage, searchString]);

  const handleDelete = async () => {
    try {
      await licensePlanApi.delete(deleteModal.planId);
      setDeleteModal({ show: false, planId: null, planName: '' });
      setSuccessMessage('License plan deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchPlans();
    } catch (err) {
      showError(err);
    }
  };

  const handleSetDefault = async (planId) => {
    try {
      await licensePlanApi.setDefault(planId);
      setSuccessMessage('Default plan updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchPlans();
    } catch (err) {
      showError(err);
    }
  };

  const formatPrice = (price) => {
    if (price == null) return '-';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  if (loading && plans.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="License Plans"
        subtitle={`Manage license plan definitions (${totalCount} total)`}
        icon={Key}
        action={
          <Button variant="primary" onClick={() => navigate('/license-plans/create')}>
            <Plus size={16} className="mr-2" />
            Create Plan
          </Button>
        }
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {successMessage && <SuccessMessage message={successMessage} onDismiss={() => setSuccessMessage(null)} />}

      <div className="mb-6">
        <SearchBar
          value={searchInput}
          onChange={(value) => setSearchInput(value)}
          placeholder="Search by name or plan code..."
        />
      </div>

      <ListContainer>
        {plans.length === 0 ? (
          <EmptyState
            icon={Key}
            title="No license plans found"
            description={searchInput ? 'Try adjusting your search' : 'Get started by creating your first license plan'}
            action={
              <Button variant="primary" onClick={() => navigate('/license-plans/create')}>
                <Plus size={16} className="mr-2" />
                Create Plan
              </Button>
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>Name</TableCell>
                  <TableCell header>Code</TableCell>
                  <TableCell header>Limits</TableCell>
                  <TableCell header>Modules</TableCell>
                  <TableCell header>Pricing</TableCell>
                  <TableCell header>Default</TableCell>
                  <TableCell header>Active</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.licensePlanId}>
                    <TableCell>
                      <div>
                        <span className="font-medium text-gray-900">{plan.name}</span>
                        {plan.description && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]">{plan.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm text-gray-700">{plan.planCode}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-700">{formatPlanLimits(plan)}</span>
                    </TableCell>
                    <TableCell>
                      <ModuleBadges modules={plan.allowedModules} variant="compact" />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {plan.monthlyPrice != null || plan.annualPrice != null ? (
                          <div className="space-y-0.5">
                            {plan.monthlyPrice != null && (
                              <div className="text-gray-700">{formatPrice(plan.monthlyPrice)}/mo</div>
                            )}
                            {plan.annualPrice != null && (
                              <div className="text-gray-500">{formatPrice(plan.annualPrice)}/yr</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {plan.isDefault ? (
                        <Badge variant="warning" size="small">
                          <Star size={12} className="mr-1" />
                          Default
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={plan.isActive ? 'success' : 'secondary'} size="small">
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/license-plans/${plan.licensePlanId}/edit`)}
                          className="text-gray-600 hover:text-primary-600 transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        {!plan.isDefault && (
                          <button
                            onClick={() => handleSetDefault(plan.licensePlanId)}
                            className="text-gray-600 hover:text-yellow-600 transition-colors"
                            title="Set as Default"
                          >
                            <Star size={16} />
                          </button>
                        )}
                        <button
                          onClick={() =>
                            setDeleteModal({ show: true, planId: plan.licensePlanId, planName: plan.name })
                          }
                          className="text-gray-600 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                />
              </div>
            )}
          </>
        )}
      </ListContainer>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, planId: null, planName: '' })}
        onConfirm={handleDelete}
        title="Delete License Plan"
        message={`Are you sure you want to delete "${deleteModal.planName}"? This action cannot be undone. Plans with active building licenses cannot be deleted.`}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default LicensePlans;
